import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, RichTextView, StatusBadge, toneForStatus } from '@ibe/ui';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { useSoftDeleteCancellationPolicy } from '../api/mutations.js';
import { useCancellationPolicyDetail } from '../api/queries.js';
import { type CancellationPolicyDetail } from '../api/schemas.js';

/**
 * One cancellation policy.
 *
 * Nothing here handles loading, errors, breadcrumbs or the correlation id —
 * `ResourceDetailPage` owns all four, so this file is only the fields this
 * resource has.
 */

export function CancellationPolicyDetailPage(): React.JSX.Element {
  const { policyId } = useParams();
  const { search } = useLocation();
  const query = useCancellationPolicyDetail(policyId ?? '');
  const softDelete = useSoftDeleteCancellationPolicy();
  const [confirming, setConfirming] = useState(false);

  return (
    <ResourceDetailPage<CancellationPolicyDetail>
      query={query}
      permission={PERMISSIONS.CANCELLATION_POLICY_READ}
      parent={{ to: '/cancellation-policies', label: 'Cancellation policies' }}
      title={(data) => data.name}
      actions={
        <>
          <PermissionGuard permission={PERMISSIONS.CANCELLATION_POLICY_UPDATE}>
            <Button asChild variant="secondary">
              <Link to={{ pathname: `/cancellation-policies/${policyId ?? ''}/edit`, search }}>
                <Pencil className="h-4 w-4" aria-hidden="true" />
                Edit
              </Link>
            </Button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.CANCELLATION_POLICY_DELETE}>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirming(true);
              }}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Delete
            </Button>
          </PermissionGuard>
        </>
      }
    >
      {(data) => (
        <>
          <DetailGrid>
            <DetailField label="Code">{data.code}</DetailField>
            <DetailField label="Status">
              <StatusBadge tone={toneForStatus(data.status)}>{data.status}</StatusBadge>
            </DetailField>
            <DetailField label="Property">
              <Link className="underline" to={`/properties/${data.propertyId}`}>
                View property
              </Link>
            </DetailField>
            <DetailField label="Summary">{data.description}</DetailField>
            <DetailField label="Version">{String(data.version)}</DetailField>
          </DetailGrid>

          <section aria-labelledby="policy-content" className="space-y-2">
            <h2 id="policy-content" className="text-body font-semibold">
              What the guest sees
            </h2>
            <div className="rounded-md border border-border p-4">
              <RichTextView
                html={data.contentHtml}
                emptyMessage="No policy document has been written yet. A guest booking this rate sees nothing here."
              />
            </div>
          </section>

          <ConfirmDialog
            open={confirming}
            onOpenChange={setConfirming}
            title="Delete cancellation policy"
            description={`${data.name} will be removed from this list and offered to no new rate plan. Rate plans that already use it keep it, and the policy can be restored.`}
            confirmLabel="Delete"
            destructive
            loading={softDelete.isPending}
            onConfirm={() => {
              softDelete.mutate(data.id, {
                onSuccess: () => {
                  setConfirming(false);
                },
              });
            }}
          />
        </>
      )}
    </ResourceDetailPage>
  );
}
